import { TutorialService } from './tutorial/tutorial.service';
import { CommonModule } from '@angular/common';
import { UsageErrorHandler } from './usage/usage-error-handler';
import { UsageTimingInterceptor } from './usage/usage-timing.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, NgModule, Optional, SkipSelf } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { JwtModule } from '@auth0/angular-jwt';
import { environment } from '../../environments/environment';

/**
 * Hosts that JwtModule should attach the bearer token to. On Capacitor-native
 * the page origin is `capacitor://localhost`, so same-origin detection fails
 * for every request to the real API — explicitly allow-listing the API host
 * (derived from environment.API_URL) makes the token attach on both web and
 * native, and for requests built with either relative or absolute URLs.
 */
function allowedApiDomains(): string[] {
  try {
    return environment.API_URL ? [new URL(environment.API_URL).host] : [];
  } catch {
    return [];
  }
}
import { BaseComponent } from './components/base/base.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { DownloadSavedSnackbarComponent } from './components/download-saved-snackbar/download-saved-snackbar.component';
import { AuthGuard } from './guards/auth.guard';
import { AiAssistantService } from './services/ai-assistant.service';
import { AIService } from './services/ai.service';
import { AnalyticsService } from './services/analytics.service';
import { AppDataService } from './services/app-data.service';
import { AppointmentTypeService } from './services/appointment-type.service';
import { AudioService } from './services/audio.service';
import { BillingService } from './services/billing.service';
import { CodingBundleService } from './services/coding-bundle.service';
import { ColleagueService } from './services/colleague.service';
import { ConsentFormsService } from './services/consent-forms.service';
import { ContactService } from './services/contact.service';
import { DeviceInfoService } from './services/device-info.service';
import { FeedbackService } from './services/feedback.service';
import { GroupService } from './services/group.service';
import { GroupInterceptor } from './interceptors/group.interceptor';
import { ApiBaseInterceptor } from './interceptors/api-base.interceptor';
import { HospitalService } from './services/hospital.service';
import { IdleService } from './services/idle.service';
import { IndexedDbService } from './services/indexed-db.service';
import { LaunchDetectionService } from './services/launch-detection.service';
import { LoaderInterceptorService, LoaderService } from './services/loader.service';
import { LogbookService } from './services/logbook.service';
import { LoggerService } from './services/logger.service';
import { MedicationService } from './services/medication.service';
import { NoteTypeService } from './services/note-type.service';
import { NotificationService } from './services/notification.service';
import { PatientAdmissionHistoryService } from './services/patient-admission-history.service';
import { PatientAiService } from './services/patient-ai.service';
import { PatientAppointmentService } from './services/patient-appointment.service';
import { PatientBillingService } from './services/patient-billing.service';
import { PatientCommunicationService } from './services/patient-communication.service';
import { PatientConsentService } from './services/patient-consent.service';
import { PatientInviteService } from './services/patient-invite.service';
import { PatientNoteService } from './services/patient-note.service';
import { PatientPrescriptionService } from './services/patient-prescription.service';
import { PatientTaskService } from './services/patient-task.service';
import { PatientService } from './services/patient.service';
import { PdfDownloadService } from './services/pdf-download.service';
import { ScheduleService } from './services/schedule.service';
import { ScreenWakeService } from './services/screen-wake.service';
import { ScrollPositionService } from './services/scroll-position.service';
import { SocketService } from './services/socket.service';
import { StateCacheService } from './services/state-cache.service';
import { SubscriptionService } from './services/subscription.service';
import { TagService } from './services/tag.service';
import { TaskService } from './services/task.service';
import { ThemeService } from './services/theme.service';
import { TokenUsageService } from './services/token-usage.service';
import { UpdateService } from './services/update.service';
import { UserService } from './services/user.service';
import { SpeechRecognitionService } from './services/speech-recognition.service';
import { FormValidationService } from './services/form-validation.service';
import { MediaService } from './services/media.service';
import { NetworkConnectivityService } from './services/network-connectivity.service';
import { SpeedTestService } from './services/speed-test.service';
import { MedpraxService } from './invoicing/medprax.invoicing.service';
import { PatientFinancalBillingService } from './services/patient-financal-billing.service';
import { ModifierService } from './modifier-rule-engine/modifier.service';
import { PatientFinancialService } from './services/patient-financial.service';
import { CodePromptingService } from './services/code-prompting.service';
import { RecordingRetryService } from './services/recording-retry.service';
import { RecordingStorageService } from './services/recording-storage.service';
import { RecordingDownloadService } from './services/recording-download.service';
import { PatientLabService } from './services/patient-lab.service';
import { WhatsAppService } from './services/whatsapp.service';

export const APP_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};


const SERVICES = [
  PdfDownloadService,
  AIService,
  UserService,
  LoaderService,
  GroupService,
  NoteTypeService,
  ConsentFormsService,
  ScheduleService,
  HospitalService,
  PatientService,
  SocketService,
  FeedbackService,
  PatientNoteService,
  PatientAppointmentService,
  AppDataService,
  PatientInviteService,
  SubscriptionService,
  AppointmentTypeService,
  ScrollPositionService,
  PatientConsentService,
  NotificationService,
  PatientAiService,
  ColleagueService,
  PatientTaskService,
  TaskService,
  TagService,
  LogbookService,
  BillingService,
  PatientBillingService,
  CodingBundleService,
  PatientAdmissionHistoryService,
  PatientCommunicationService,
  IdleService,
  StateCacheService,
  ScreenWakeService,
  LoggerService,
  LaunchDetectionService,
  UpdateService,
  DeviceInfoService,
  TokenUsageService,
  IndexedDbService,
  AudioService,
  ContactService,
  MedicationService,
  AnalyticsService,
  PatientPrescriptionService,
  ThemeService,
  AiAssistantService,
  SpeechRecognitionService,
  FormValidationService,
  TutorialService,
  MediaService,
  NetworkConnectivityService,
  SpeedTestService,
  MedpraxService,
  PatientFinancalBillingService,
  ModifierService,
  PatientFinancialService,
  CodePromptingService,
  RecordingRetryService,
  RecordingStorageService,
  RecordingDownloadService,
  CodePromptingService,
  PatientLabService,
  WhatsAppService
]

@NgModule({
  declarations: [
    ConfirmationDialogComponent,
    DownloadSavedSnackbarComponent,
  ],
  imports: [
    CommonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: () => localStorage.getItem('token'),
        allowedDomains: allowedApiDomains()
      }
    })
  ],
  providers: [
    ...SERVICES,
    BaseComponent,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UsageTimingInterceptor,
      multi: true
    },
    { provide: ErrorHandler, useClass: UsageErrorHandler },
    /* {
      provide: HTTP_INTERCEPTORS,
      useClass: GroupInterceptor,
      multi: true
    }, */
    AuthGuard,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() core: CoreModule) {
    if (core) {
      throw new Error("CoreModule should only be imported into AppModule");
    }
  }
}
